import React from 'react'
import { getDevicePixelRatio } from '../helpers/device-pixel'

export type MarginGuideEdge = 'top' | 'bottom' | 'left' | 'right'

/** Vertical margin segment at the spine (gutter) for this page half. */
export function spineMarginEdgeForPage(insideOnLeft: boolean): MarginGuideEdge {
  return insideOnLeft ? 'left' : 'right'
}

/** Top, bottom, and the outer (non-spine) vertical — drawn above shapes on spreads. */
export function nonSpineMarginEdgesForPage(insideOnLeft: boolean): MarginGuideEdge[] {
  const spine = spineMarginEdgeForPage(insideOnLeft)

  return (['top', 'bottom', 'left', 'right'] as const).filter((e) => e !== spine)
}

type MarginsOverlayProps = {
  // Trim rect (absolute within spread)
  trimLeft: number
  trimTop: number
  trimWidth: number
  trimHeight: number
  // Margins inside trim
  inside: number // inner margin (spine side)
  outside: number // outer margin
  top: number
  bottom: number
  // Spine on left or right? true => inside on left, false => inside on right
  insideOnLeft?: boolean
  /** Omit to draw all four edges (single-page and legacy). */
  edges?: MarginGuideEdge[]
  /**
   * When set, applied to each line segment. Use for “above shapes” margins without wrapping in a
   * full-rect `z-index` layer (which would sit above the bleed and hide it behind compositing).
   */
  lineZIndex?: number
  /** Canvas zoom scale factor so 1 physical-pixel hairlines stay crisp at fractional zoom. */
  scale?: number
}

const MarginsOverlay: React.FC<MarginsOverlayProps> = ({
  trimLeft,
  trimTop,
  trimWidth,
  trimHeight,
  inside,
  outside,
  top,
  bottom,
  insideOnLeft = true,
  edges,
  lineZIndex,
  scale = 1,
}) => {
  const leftInset = insideOnLeft ? inside : outside
  const rightInset = insideOnLeft ? outside : inside

  const innerLeft = trimLeft + leftInset
  const innerTop = trimTop + top
  const innerRight = trimLeft + trimWidth - rightInset
  const innerBottom = trimTop + trimHeight - bottom
  const effectiveScale = Number.isFinite(scale) && scale > 0 ? scale : 1
  const pixelsPerCanvasUnit = effectiveScale * getDevicePixelRatio()
  const hairlineThickness = 1 / pixelsPerCanvasUnit
  const snapToDevicePixel = (value: number) =>
    Math.round(value * pixelsPerCanvasUnit) / pixelsPerCanvasUnit
  const snappedLeft = snapToDevicePixel(innerLeft)
  const snappedTop = snapToDevicePixel(innerTop)
  const snappedRight = snapToDevicePixel(innerRight)
  const snappedBottom = snapToDevicePixel(innerBottom)

  const allEdges: MarginGuideEdge[] = ['top', 'bottom', 'left', 'right']
  const active = edges ?? allEdges
  const show = (e: MarginGuideEdge) => active.includes(e)

  /*
   * A single bordered box, not four independently-positioned hairline `<div>`s. Four separate
   * elements each round their own position to the device-pixel grid independently
   * (`snapToDevicePixel`), so a corner built from two of them meeting edge-to-edge could land on
   * adjacent-but-not-touching pixels at some zoom levels, leaving a hairline gap right at the
   * corner — confirmed live via devtools, and persisted even after deliberately overlapping each
   * line past its own end. A CSS border draws all four sides as one shape, so the browser joins
   * the corners itself; there is no second independently-rounded element for a gap to open up
   * between. `border-box` sizing keeps the border's outer edge exactly at the snapped box (the
   * same "inside" placement the old fill-rect lines used), and a zero-width side for an omitted
   * edge draws nothing while leaving the adjacent corners intact.
   */
  return (
    <div
      style={{
        position: 'absolute',
        left: snappedLeft,
        top: snappedTop,
        width: Math.max(0, snappedRight - snappedLeft),
        height: Math.max(0, snappedBottom - snappedTop),
        boxSizing: 'border-box',
        pointerEvents: 'none',
        borderStyle: 'solid',
        borderColor: 'var(--doc-margin-line)',
        borderTopWidth: show('top') ? hairlineThickness : 0,
        borderBottomWidth: show('bottom') ? hairlineThickness : 0,
        borderLeftWidth: show('left') ? hairlineThickness : 0,
        borderRightWidth: show('right') ? hairlineThickness : 0,
        ...(lineZIndex != null ? { zIndex: lineZIndex } : {}),
      }}
    />
  )
}

export default MarginsOverlay
